import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ApiError } from "@/api/client";
import {
  useCheckRelationship,
  useConnections,
  useConnectionSchema,
  useCreateRelationship,
  useDeleteRelationship,
  useRelationships,
  useRelationshipSuggestions,
  useUpdateRelationship,
  type Relationship,
  type RelationshipOverlap,
  type RelationshipWrite,
} from "@/api/queries";
import { Button, IconButton } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { EmptyState } from "@/components/ui/EmptyState";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import { Textarea } from "@/components/ui/Textarea";
import { LinkIcon, PencilIcon, PlusIcon, SparkIcon, TrashIcon } from "@/components/icons";

/**
 * How this source joins the others (PRD FR-1.9, MS-1).
 *
 * A declaration is knowledge the multi-source prompt presents as the
 * authoritative join key, so it is edited the way the data dictionary is:
 * Admins only, every save a diff in the audit. Each row is shown from *this*
 * source's side — this column first — whichever order it was declared in.
 */
export function RelationshipsPanel({
  connectionId,
  readOnly,
}: {
  connectionId: string;
  readOnly: boolean;
}) {
  const { data: rows, isLoading } = useRelationships(connectionId);
  const remove = useDeleteRelationship();
  const [editing, setEditing] = useState<Relationship | "new" | null>(null);
  const [deleting, setDeleting] = useState<Relationship | null>(null);

  return (
    <div className="flex flex-col gap-6">
      <div className="border-border bg-surface rounded-container border p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <span className="type-h4 text-ink inline-flex items-center gap-2">
              <LinkIcon className="size-4" />
              Relationships with other sources
            </span>
            <p className="type-support text-ink-secondary mt-2">
              A relationship says which column here matches a column in another source. When a
              conversation uses both sources, these are the join keys the assistant is told to
              trust — so declare only what is true of the data.
            </p>
          </div>
          {readOnly ? null : (
            <Button
              variant="primary"
              icon={<PlusIcon className="size-4" />}
              onClick={() => setEditing("new")}
            >
              Declare relationship
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <Spinner />
      ) : !rows || rows.length === 0 ? (
        <EmptyState
          icon={<LinkIcon className="size-6" />}
          title="No relationships declared"
          detail={
            readOnly
              ? "An administrator can declare how this source joins the others."
              : "Declare one to let a conversation that uses this source and another join them on a key you have checked."
          }
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {rows.map((row) => (
            <RelationshipRow
              key={row.id}
              row={row}
              connectionId={connectionId}
              readOnly={readOnly}
              onEdit={() => setEditing(row)}
              onDelete={() => setDeleting(row)}
            />
          ))}
        </ul>
      )}

      {editing ? (
        <RelationshipDialog
          connectionId={connectionId}
          existing={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Remove this relationship?"
        description={deleting ? deleting.summary.split(" — ")[0] : ""}
        consequence="Conversations that use both sources will no longer be told this join key. Answers already given keep their trace."
        confirmLabel="Remove"
        loading={remove.isPending}
        onConfirm={() =>
          deleting && remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
        }
      />
    </div>
  );
}

function sides(row: Relationship, connectionId: string) {
  return row.left.connection_id === connectionId
    ? { here: row.left, there: row.right }
    : { here: row.right, there: row.left };
}

function RelationshipRow({
  row,
  connectionId,
  readOnly,
  onEdit,
  onDelete,
}: {
  row: Relationship;
  connectionId: string;
  readOnly: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { here, there } = sides(row, connectionId);
  return (
    <li className="border-border bg-card rounded-container flex items-start gap-3 border p-4">
      <div className="min-w-0 flex-1">
        <p className="type-body text-ink flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-mono">
            {here.relation}.{here.column}
          </span>
          <span aria-hidden className="text-ink-disabled">
            ↔
          </span>
          <span className="sr-only">matches</span>
          <Link
            to="/sources/$connectionId"
            params={{ connectionId: there.connection_id }}
            search={{ tab: "relationships" }}
            className="text-accent hover:underline"
          >
            {there.connection_name}
          </Link>
          <span className="font-mono">
            {there.relation}.{there.column}
          </span>
          {row.stale ? (
            <Chip tone="warning">
              {(here.stale ?? there.stale) === "relation_missing"
                ? "relation no longer in the schema"
                : "column no longer in the schema"}
            </Chip>
          ) : null}
        </p>
        {row.note ? (
          <p className="type-support text-ink-secondary mt-1">{row.note}</p>
        ) : (
          <p className="type-support text-ink-disabled mt-1">No note.</p>
        )}
        {row.stale ? (
          <p className="type-support text-warning mt-1">
            Left out of the prompt until it matches the schema again.
          </p>
        ) : null}
      </div>
      {readOnly ? null : (
        <div className="flex shrink-0 gap-1">
          <IconButton label="Edit relationship" onClick={onEdit}>
            <PencilIcon className="size-4" />
          </IconButton>
          <IconButton label="Remove relationship" onClick={onDelete}>
            <TrashIcon className="size-4" />
          </IconButton>
        </div>
      )}
    </li>
  );
}

type Draft = {
  otherId: string;
  hereRelation: string;
  hereColumn: string;
  thereRelation: string;
  thereColumn: string;
  note: string;
};

function qualified(relation: { name: string; schema_name?: string | null }): string {
  return relation.schema_name ? `${relation.schema_name}.${relation.name}` : relation.name;
}

/** Declare or edit one relationship, with suggestions and the overlap check. */
function RelationshipDialog({
  connectionId,
  existing,
  onClose,
}: {
  connectionId: string;
  existing: Relationship | null;
  onClose: () => void;
}) {
  const { data: connections } = useConnections();
  const create = useCreateRelationship();
  const update = useUpdateRelationship();
  const check = useCheckRelationship();
  const [overlap, setOverlap] = useState<RelationshipOverlap | null>(null);

  const initial: Draft = existing
    ? (() => {
        const { here, there } = sides(existing, connectionId);
        return {
          otherId: there.connection_id,
          hereRelation: here.relation,
          hereColumn: here.column,
          thereRelation: there.relation,
          thereColumn: there.column,
          note: existing.note,
        };
      })()
    : { otherId: "", hereRelation: "", hereColumn: "", thereRelation: "", thereColumn: "", note: "" };
  const [draft, setDraft] = useState<Draft>(initial);
  const set = (patch: Partial<Draft>) => {
    setOverlap(null);
    check.reset();
    setDraft((current) => ({ ...current, ...patch }));
  };

  const others = (connections ?? []).filter((row) => row.id !== connectionId);
  const hereSchema = useConnectionSchema(connectionId);
  const thereSchema = useConnectionSchema(draft.otherId || connectionId);
  const suggestions = useRelationshipSuggestions(connectionId, draft.otherId, !existing);

  const columnsOf = (
    schema: typeof hereSchema.data,
    relation: string,
  ): string[] =>
    schema?.relations
      .find((entry) => qualified(entry) === relation)
      ?.columns?.map((column) => column.column_name) ?? [];

  const body: RelationshipWrite | null =
    draft.otherId && draft.hereRelation && draft.hereColumn && draft.thereRelation && draft.thereColumn
      ? {
          left: { connection_id: connectionId, relation: draft.hereRelation, column: draft.hereColumn },
          right: {
            connection_id: draft.otherId,
            relation: draft.thereRelation,
            column: draft.thereColumn,
          },
          note: draft.note,
        }
      : null;

  const saving = create.isPending || update.isPending;
  const failure = create.error ?? update.error;
  const save = () => {
    if (!body) return;
    if (existing) {
      update.mutate(
        { id: existing.id, left: body.left, right: body.right, note: body.note },
        { onSuccess: onClose },
      );
    } else {
      create.mutate(body, { onSuccess: onClose });
    }
  };

  const otherName = others.find((row) => row.id === draft.otherId)?.name ?? "the other source";
  const thereRelations = draft.otherId ? (thereSchema.data?.relations ?? []) : [];
  const hereRelations = hereSchema.data?.relations ?? [];

  const visibleSuggestions = useMemo(
    () => (existing ? [] : (suggestions.data ?? []).slice(0, 6)),
    [existing, suggestions.data],
  );

  return (
    <Modal
      open
      wide
      onClose={onClose}
      title={existing ? "Edit relationship" : "Declare a relationship"}
      description="Which column here holds the same values as a column in another source."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!body} loading={saving} onClick={save}>
            {existing ? "Save" : "Declare"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <Select
          label="Other source"
          value={draft.otherId}
          disabled={Boolean(existing)}
          onChange={(event) =>
            set({ otherId: event.target.value, thereRelation: "", thereColumn: "" })
          }
        >
          <option value="">Choose a source…</option>
          {others.map((row) => (
            <option key={row.id} value={row.id}>
              {row.name}
            </option>
          ))}
        </Select>

        {draft.otherId && !existing ? (
          <div>
            <p className="type-label text-ink inline-flex items-center gap-1.5">
              <SparkIcon className="size-3.5" />
              Suggested from the two schemas
            </p>
            {suggestions.isLoading ? (
              <Spinner />
            ) : visibleSuggestions.length === 0 ? (
              <p className="type-support text-ink-secondary mt-1">
                No column names line up. Pick the columns yourself below.
              </p>
            ) : (
              <ul className="mt-2 flex flex-col gap-1.5">
                {visibleSuggestions.map((item) => (
                  <li key={`${item.left.relation}.${item.left.column}~${item.right.relation}.${item.right.column}`}>
                    <button
                      type="button"
                      onClick={() =>
                        set({
                          hereRelation: item.left.relation,
                          hereColumn: item.left.column,
                          thereRelation: item.right.relation,
                          thereColumn: item.right.column,
                        })
                      }
                      className="border-border hover:border-border-emphasized hover:bg-hover rounded-element motion-fast flex w-full flex-wrap items-center gap-x-2 border px-3 py-2 text-start"
                    >
                      <span className="type-support text-ink font-mono">
                        {item.left.relation}.{item.left.column} ↔ {item.right.relation}.
                        {item.right.column}
                      </span>
                      <span className="type-support text-ink-secondary">{item.reason}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <fieldset className="flex flex-col gap-3">
            <legend className="type-label text-ink mb-2">This source</legend>
            <Select
              label="Table or view"
              value={draft.hereRelation}
              onChange={(event) => set({ hereRelation: event.target.value, hereColumn: "" })}
            >
              <option value="">Choose…</option>
              {hereRelations.map((relation) => (
                <option key={qualified(relation)} value={qualified(relation)}>
                  {qualified(relation)}
                </option>
              ))}
            </Select>
            <Select
              label="Column"
              value={draft.hereColumn}
              disabled={!draft.hereRelation}
              onChange={(event) => set({ hereColumn: event.target.value })}
            >
              <option value="">Choose…</option>
              {columnsOf(hereSchema.data, draft.hereRelation).map((column) => (
                <option key={column} value={column}>
                  {column}
                </option>
              ))}
            </Select>
          </fieldset>
          <fieldset className="flex flex-col gap-3" disabled={!draft.otherId}>
            <legend className="type-label text-ink mb-2">{otherName}</legend>
            <Select
              label="Table or view"
              value={draft.thereRelation}
              onChange={(event) => set({ thereRelation: event.target.value, thereColumn: "" })}
            >
              <option value="">Choose…</option>
              {thereRelations.map((relation) => (
                <option key={qualified(relation)} value={qualified(relation)}>
                  {qualified(relation)}
                </option>
              ))}
            </Select>
            <Select
              label="Column"
              value={draft.thereColumn}
              disabled={!draft.thereRelation}
              onChange={(event) => set({ thereColumn: event.target.value })}
            >
              <option value="">Choose…</option>
              {columnsOf(thereSchema.data, draft.thereRelation).map((column) => (
                <option key={column} value={column}>
                  {column}
                </option>
              ))}
            </Select>
          </fieldset>
        </div>

        <Textarea
          label="Note"
          rows={3}
          maxLength={1000}
          value={draft.note}
          onChange={(event) => set({ note: event.target.value })}
          hint="What the key means and at what grain — e.g. “both are ISO dates; conditions are one row per break per day.” The assistant reads this."
        />

        <div className="flex flex-wrap items-center gap-3">
          <Button
            disabled={!body}
            loading={check.isPending}
            onClick={() => body && check.mutate(body, { onSuccess: setOverlap })}
          >
            Check overlap
          </Button>
          <span className="type-support text-ink-secondary">
            Reads up to 1,000 distinct values from each side. Both reads are in the SQL audit.
          </span>
        </div>
        {check.error ? (
          <p role="alert" className="type-support text-error rounded-element bg-error-subtle p-3">
            {check.error instanceof ApiError ? check.error.message : "The check could not run."}
          </p>
        ) : null}
        {overlap ? <OverlapVerdict overlap={overlap} otherName={otherName} /> : null}

        {failure ? (
          <p role="alert" className="type-support text-error rounded-element bg-error-subtle p-3">
            {failure instanceof ApiError ? failure.message : "The relationship could not be saved."}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

function OverlapVerdict({
  overlap,
  otherName,
}: {
  overlap: RelationshipOverlap;
  otherName: string;
}) {
  const share =
    overlap.left_distinct > 0 ? overlap.normalised_matches / overlap.left_distinct : 0;
  const tone = share >= 0.8 ? "success" : share > 0 ? "warning" : "error";
  return (
    <div
      role="status"
      className="border-border bg-surface rounded-element flex flex-col gap-1.5 border p-3"
    >
      <p className="type-label text-ink flex flex-wrap items-center gap-2">
        <Chip tone={tone}>
          {overlap.normalised_matches} of {overlap.left_distinct} values match
        </Chip>
        <span className="type-support text-ink-secondary">
          {overlap.left_distinct} distinct here{overlap.left_sampled_all ? "" : " (sampled)"},{" "}
          {overlap.right_distinct} in {otherName}
          {overlap.right_sampled_all ? "" : " (sampled)"}
        </span>
      </p>
      {overlap.normalised_matches > overlap.exact_matches ? (
        <p className="type-support text-ink-secondary">
          Only {overlap.exact_matches} match exactly — the rest match once case, spacing or number
          formatting is ignored, so a join needs a CAST or a LOWER on both sides.
        </p>
      ) : null}
      {overlap.examples.length > 0 ? (
        <p className="type-support text-ink-secondary">
          Matching: <span className="font-mono">{overlap.examples.join(", ")}</span>
        </p>
      ) : null}
      {overlap.unmatched_left.length > 0 ? (
        <p className="type-support text-ink-secondary">
          No match: <span className="font-mono">{overlap.unmatched_left.join(", ")}</span>
        </p>
      ) : null}
    </div>
  );
}
